import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import axiosInstance from '../api/axios';
import { toast } from 'sonner';
import { Mail, Send, Facebook, Instagram, Smartphone, Download } from 'lucide-react';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [contactSettings, setContactSettings] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axiosInstance.get('/contact-settings');
        setContactSettings(response.data);
      } catch (error) {
        console.error('Error fetching contact settings:', error);
      }
    };
    fetchSettings();
  }, []);

  // PWA Install Handler
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      // Show install button
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.info('App ist bereits installiert oder Installation nicht verfügbar');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      toast.success('App wird installiert...');
    } else {
      toast.info('Installation abgebrochen');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setShowInstallButton(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axiosInstance.post('/contact', formData);
      toast.success('Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Fehler beim Senden der Nachricht');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="kontakt" className="py-16 px-4" style={{ backgroundColor: '#E8E0D5' }}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#6D5A47' }}>
            Kontaktieren Sie uns
          </h2>
          <p className="text-lg text-gray-600">
            Haben Sie Fragen? Wir helfen Ihnen gerne weiter!
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Information */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ color: '#6D5A47' }}>Kontaktinformationen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* E-Mail */}
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg accent-mint">
                    <Mail className="h-5 w-5" style={{ color: '#8B7355' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">E-Mail</h3>
                    <a href={`mailto:${contactSettings?.email || 'kontakt@homeyoursoul.de'}`} className="text-gray-600 hover:text-[#C4A084] transition-colors">
                      {contactSettings?.email || 'kontakt@homeyoursoul.de'}
                    </a>
                  </div>
                </div>

                {/* Social Media mit Pastellfarben - IMMER ANZEIGEN */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3 text-center">Folgen Sie uns</h3>
                  <div className="flex gap-3 justify-center">
                    {/* Facebook */}
                    <a 
                      href={contactSettings?.facebook_url || '#'}
                      target={contactSettings?.facebook_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-full accent-sky hover:accent-sky-hover flex items-center justify-center transition-all hover:scale-110 shadow-md ${!contactSettings?.facebook_url && 'opacity-50 cursor-not-allowed'}`}
                      aria-label="Facebook"
                      onClick={(e) => {
                        if (!contactSettings?.facebook_url) {
                          e.preventDefault();
                          toast.info('Facebook-Link wird vom Admin noch hinzugefügt');
                        }
                      }}
                    >
                      <svg className="w-6 h-6" fill="#8B7355" viewBox="0 0 24 24">
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      </svg>
                    </a>
                    
                    {/* Instagram */}
                    <a 
                      href={contactSettings?.instagram_url || '#'}
                      target={contactSettings?.instagram_url ? "_blank" : "_self"}
                      rel="noopener noreferrer"
                      className={`w-12 h-12 rounded-full accent-rose hover:accent-rose-hover flex items-center justify-center transition-all hover:scale-110 shadow-md ${!contactSettings?.instagram_url && 'opacity-50 cursor-not-allowed'}`}
                      aria-label="Instagram"
                      onClick={(e) => {
                        if (!contactSettings?.instagram_url) {
                          e.preventDefault();
                          toast.info('Instagram-Link wird vom Admin noch hinzugefügt');
                        }
                      }}
                    >
                      <svg className="w-6 h-6" fill="#8B7355" viewBox="0 0 24 24">
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      </svg>
                    </a>
                    
                    {/* TikTok - Optional anzeigen */}
                    {contactSettings?.tiktok_url && (
                      <a 
                        href={contactSettings.tiktok_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="w-12 h-12 rounded-full accent-yellow hover:accent-yellow-hover flex items-center justify-center transition-all hover:scale-110 shadow-md"
                        aria-label="TikTok"
                      >
                        <svg className="w-6 h-6" fill="#8B7355" viewBox="0 0 24 24">
                          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                        </svg>
                      </a>
                    )}
                  </div>
                </div>

                {/* QR Code Section für App Download */}
                <div className="pt-4 border-t">
                  <h3 className="font-semibold mb-3 text-center flex items-center justify-center gap-2">
                    <Smartphone className="h-5 w-5" style={{ color: '#8B7355' }} />
                    App herunterladen
                  </h3>
                  <div className="flex flex-col items-center">
                    <div 
                      className="bg-white p-4 rounded-lg shadow-md border-2 border-[#F5E6A0] cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={handleInstallClick}
                      title="Klicken zum Installieren der App"
                    >
                      <QRCodeSVG 
                        value={window.location.origin}
                        size={120}
                        level="H"
                        includeMargin={true}
                        fgColor="#8B7355"
                      />
                    </div>
                    
                    {/* Install Button - nur anzeigen wenn PWA installierbar ist */}
                    {showInstallButton && (
                      <Button
                        onClick={handleInstallClick}
                        className="mt-3 text-white hover:scale-105 transition-transform"
                        
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Jetzt installieren
                      </Button>
                    )}
                    
                    <p className="text-xs text-center text-gray-600 mt-3 max-w-xs">
                      📱 <strong>Zum Installieren:</strong> {showInstallButton ? 'Klicken Sie auf den QR-Code oder den Button oben' : 'Scannen Sie diesen QR-Code mit Ihrer Handy-Kamera'}, um unsere App zu installieren - schnell und ohne App Store!
                    </p>
                  </div>
                </div>

                {/* Info Text */}
                <div className="pt-4 border-t">
                  <p className="text-sm text-gray-600">
                    📨 Senden Sie uns eine Nachricht und wir melden uns schnellstmöglich bei Ihnen zurück!
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Contact Form */}
          <Card>
            <CardHeader>
              <CardTitle style={{ color: '#6D5A47' }}>Schreiben Sie uns</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    required
                    placeholder="Ihr Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="ihre@email.de"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="subject">Betreff *</Label>
                  <Input
                    id="subject"
                    required
                    placeholder="Worum geht es?"
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>

                <div>
                  <Label htmlFor="message">Nachricht *</Label>
                  <Textarea
                    id="message"
                    required
                    placeholder="Ihre Nachricht an uns..."
                    rows={6}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full text-white"
                  
                >
                  <Send className="h-4 w-4 mr-2" />
                  {loading ? 'Wird gesendet...' : 'Nachricht senden'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
